import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import AuthShell from "@/components/AuthShell";
import PasswordInput from "@/components/PasswordInput";
import { UsersThree, SpinnerGap, WarningCircle } from "@phosphor-icons/react";

const inputCls = "w-full bg-surface-2 border border-border rounded-md px-4 py-3 text-fg text-sm focus:outline-none focus:ring-2 focus:ring-accent/60 focus:border-accent/40 transition-shadow";

export default function AcceptInvite() {
  const { token } = useParams();
  const navigate = useNavigate();
  const { login } = useAuth();
  const [state, setState] = useState({ status: "loading" }); // loading | valid | invalid
  const [name, setName] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/invites/${token}`);
        if (data.valid) setState({ status: "valid", ...data });
        else setState({ status: "invalid", reason: data.reason });
      } catch {
        setState({ status: "invalid" });
      }
    })();
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const { data } = await api.post(`/invites/${token}/accept`, { name, password });
      login(data.token, data.user);
      toast.success("Bienvenue dans l'équipe !");
      navigate("/app");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSubmitting(false);
    }
  };

  if (state.status === "loading") {
    return (
      <AuthShell title="Invitation" subtitle="Vérification de votre lien d'invitation…">
        <div className="flex items-center gap-3 text-muted" data-testid="invite-loading">
          <SpinnerGap size={22} className="animate-spin text-accent" /> Un instant…
        </div>
      </AuthShell>
    );
  }

  if (state.status === "invalid") {
    return (
      <AuthShell
        title="Invitation invalide"
        subtitle="Ce lien n'est plus valide."
        footer={<>Déjà un compte ? <Link to="/login" className="text-accent hover:underline" data-testid="invite-link-login">Se connecter</Link></>}
      >
        <div className="surface rounded-lg p-5 flex items-start gap-3" data-testid="invite-invalid">
          <WarningCircle size={22} className="text-accent shrink-0 mt-0.5" weight="fill" />
          <p className="text-sm text-muted">
            {state.reason === "account_exists"
              ? "Un compte existe déjà avec cet email. Connectez-vous directement."
              : "Cette invitation est invalide, a expiré ou a déjà été utilisée. Demandez à votre collègue de vous renvoyer une invitation."}
          </p>
        </div>
      </AuthShell>
    );
  }

  return (
    <AuthShell
      title="Rejoindre l'équipe"
      subtitle={`${state.inviter_name || "Un collègue"} vous invite à rejoindre ${state.org_name || "son organisation"} sur interKO.`}
      footer={<>Déjà un compte ? <Link to="/login" className="text-accent hover:underline" data-testid="invite-link-login">Se connecter</Link></>}
    >
      <div className="surface rounded-lg p-4 mb-6 flex items-center gap-3" data-testid="invite-org-banner">
        <div className="w-10 h-10 rounded-md bg-accent/15 border border-accent/30 flex items-center justify-center text-accent shrink-0">
          <UsersThree size={20} weight="duotone" />
        </div>
        <div className="min-w-0">
          <div className="text-sm text-fg font-medium truncate">{state.org_name}</div>
          <div className="text-xs text-muted truncate">{state.email}</div>
        </div>
      </div>

      <form onSubmit={submit} className="space-y-4" data-testid="invite-form">
        <div>
          <label className="overline block mb-2">Votre nom</label>
          <input type="text" required value={name} onChange={(e) => setName(e.target.value)}
            className={inputCls} placeholder="Prénom Nom" data-testid="invite-name" />
        </div>
        <div>
          <label className="overline block mb-2">Mot de passe</label>
          <PasswordInput value={password} onChange={(e) => setPassword(e.target.value)} required testid="invite-password" />
          <p className="text-xs text-muted mt-2">Au moins 11 caractères, avec majuscule, minuscule, chiffre et caractère spécial.</p>
        </div>
        <button type="submit" disabled={submitting} data-testid="invite-submit"
          className="w-full bg-accent text-black font-semibold py-3 rounded-md hover-lift disabled:opacity-60">
          {submitting ? "Création du compte…" : "Rejoindre l'équipe"}
        </button>
      </form>
    </AuthShell>
  );
}
